import IPoint from 'src/types/point';
import { Vector, Box, Polygon } from 'sat';

import { absMin } from 'src/utils/math';

export function snapToGrid(point: IPoint, cellSize: number): IPoint {
	if (cellSize === 0) return point;

	return {
		x: Math.round(point.x / cellSize) * cellSize,
		y: Math.round(point.y / cellSize) * cellSize,
	};
}

export function snapBoxToGrid(box: Box, cellSize: number): Vector {
	if (cellSize === 0) return new Vector(0, 0);

	const snappedTopLeft = snapToGrid(box.pos, cellSize);
	const topLeftDisplacement = new Vector(snappedTopLeft.x, snappedTopLeft.y).sub(box.pos);

	const bottomRight = new Vector(box.w, box.h).add(box.pos);
	const snappedBottomRight = snapToGrid(bottomRight, cellSize);
	const bottomRightDisplacement = new Vector(snappedBottomRight.x, snappedBottomRight.y).sub(bottomRight);

	const displacement = new Vector(
		absMin(topLeftDisplacement.x, bottomRightDisplacement.x),
		absMin(topLeftDisplacement.y, bottomRightDisplacement.y),
	);

	return displacement;
}

export function boxFromPolygons(polygons: Array<Polygon>): Box {
	// @ts-expect-error
	const { topLeft, bottomRight } = polygons.map((polygon) => polygon.getAABBAsBox())
		.reduce(({ topLeft: topLeft_, bottomRight: bottomRight_ }, box) => {
			return {
				topLeft: {
					x: Math.min(topLeft_.x, box.pos.x),
					y: Math.min(topLeft_.y, box.pos.y),
				},
				bottomRight: {
					x: Math.max(bottomRight_.x, box.pos.x + box.w),
					y: Math.max(bottomRight_.y, box.pos.y + box.h),
				},
			};
		}, {
			topLeft: {
				x: Infinity,
				y: Infinity,
			},
			bottomRight: {
				x: -Infinity,
				y: -Infinity,
			},
		});

	return new Box(new Vector(topLeft.x, topLeft.y), bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
}

export function pointSegmentDistanceSquared(seg1: IPoint, seg2: IPoint, point: IPoint): number {
	const segment = new Vector(seg1.x - seg2.x, seg1.y - seg2.y);
	const a = new Vector(seg1.x - point.x, seg1.y - point.y);

	const segMagSquared = segment.len2();
	let t = a.dot(segment) / segMagSquared;
	// clamp to turn the line into a segment
	t = Math.min(Math.max(t, 0), 1);

	const projection = segment.scale(t);

	// distance (squared) between projection and a
	// (note that projection gets modified in place but that's fine, we don't need it anymore)
	return projection.sub(a).len2();
}

// https://stackoverflow.com/a/6865965/3489880
export function pointsAligned(point1: IPoint, point2: IPoint, point3: IPoint): boolean {
	return (point3.y - point1.y)  * (point2.x - point1.x) === (point2.y - point1.y) * (point3.x - point1.x);
}
